import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getListingDetail } from '../services/api';

export default function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getListingDetail(parseInt(id, 10))
      .then(data => {
        setListing(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Fehler beim Laden');
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <p>Inserat wird geladen...</p>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="error-page">
        <h2>Fehler</h2>
        <p>{error || 'Inserat nicht gefunden'}</p>
        <button className="btn btn--primary" onClick={() => navigate(-1)}>Zurück</button>
      </div>
    );
  }

  const price = listing.price_chf
    ? `CHF ${Number(listing.price_chf).toLocaleString('de-CH')}`
    : listing.rent_gross_chf
    ? `CHF ${Number(listing.rent_gross_chf).toLocaleString('de-CH')}/Mt.`
    : 'Preis auf Anfrage';

  const description = listing.description_de || listing.description_en || listing.description_fr || '';

  return (
    <div className="detail-page">
      <button className="btn btn--back" onClick={() => navigate(-1)}>
        ← Zurück zu den Ergebnissen
      </button>

      {/* Image gallery */}
      {listing.images?.length > 0 && (
        <div className="detail-images">
          {listing.images.slice(0, 6).map((img: any, i: number) => (
            <img key={i} src={img.image_url} alt={`Bild ${i + 1}`} className="detail-image" />
          ))}
        </div>
      )}

      <div className="detail-content">
        <div className="detail-main">
          <h1 className="detail-title">{listing.title || 'Immobilie'}</h1>
          <p className="detail-location">
            {[listing.address, listing.zip_code, listing.city, listing.canton].filter(Boolean).join(', ')}
          </p>

          <div className="detail-price-box">
            <span className="detail-price">{price}</span>
            {listing.price_per_m2 && (
              <span className="detail-price-per-m2">CHF {Number(listing.price_per_m2).toLocaleString('de-CH')}/m²</span>
            )}
          </div>

          {/* Details grid */}
          <div className="detail-grid">
            {listing.rooms && <div className="detail-item"><span className="detail-label">Zimmer</span><span className="detail-value">{listing.rooms}</span></div>}
            {listing.living_space_m2 && <div className="detail-item"><span className="detail-label">Wohnfläche</span><span className="detail-value">{listing.living_space_m2} m²</span></div>}
            {listing.plot_size_m2 && <div className="detail-item"><span className="detail-label">Grundstück</span><span className="detail-value">{listing.plot_size_m2} m²</span></div>}
            {listing.property_type && <div className="detail-item"><span className="detail-label">Typ</span><span className="detail-value">{listing.property_type}</span></div>}
            {listing.year_built && <div className="detail-item"><span className="detail-label">Baujahr</span><span className="detail-value">{listing.year_built}</span></div>}
            {listing.floor_level != null && <div className="detail-item"><span className="detail-label">Stockwerk</span><span className="detail-value">{listing.floor_level}</span></div>}
          </div>

          {/* Features */}
          {listing.features && Object.keys(listing.features).length > 0 && (
            <div className="detail-features">
              <h3>Ausstattung</h3>
              <div className="feature-list">
                {Object.entries(listing.features).map(([key, val]: [string, any]) => (
                  <span key={key} className="feature-tag">{key}</span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {description && (
            <div className="detail-description">
              <h3>Beschreibung</h3>
              <p>{description}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="detail-sidebar">
          {listing.agency_name && (
            <div className="agency-card">
              <h4>Agentur</h4>
              <p>{listing.agency_name}</p>
              {listing.contact_person && <p>{listing.contact_person}</p>}
              {listing.agency_phone && <p>{listing.agency_phone}</p>}
              {listing.agency_email && <p>{listing.agency_email}</p>}
            </div>
          )}
          {listing.source_url && (
            <a href={listing.source_url} target="_blank" rel="noopener noreferrer" className="btn btn--primary btn--full">
              Zum Original-Inserat →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
